import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import UndoIcon from '@mui/icons-material/Undo';
import { contractTagApi, featureApi } from '../../api/endpoints';
import { acceptanceApi, changeRequestsApi, confirmationImagesApi } from '../../api/acceptanceApi';
import { getApiErrorMessage, resolveConfirmationImageUrl, resolveImageUrl } from '../../api/client';
import { notify } from '../../components/Notifier';
import { TicketCodeBadge } from '../../components/TicketCodeBadge';
import { StatusBadge } from '../../components/StatusBadge';
import { ImageViewer } from '../../components/ImageViewer';
import { ImageUploader } from '../../components/ImageUploader';
import { SavedImageGrid } from '../../components/SavedImageGrid';
import { Can } from '../../auth/Can';
import { usePermission } from '../../auth/usePermission';
import { FEATURE_STATUS_META } from '../Admin/statusMeta';
import { CHANGE_REQUEST_STATUS_META } from '../ChangeRequests/statusMeta';
import { ChangeRequestDetail } from '../ChangeRequests/ChangeRequestDetail';
import { ConfirmDialog } from '../Admin/ConfirmDialog';
import { FeatureBugsSection } from '../Bugs/FeatureBugsSection';
import { FEATURE_FIXING_META } from '../Bugs/statusMeta';
import { formatDateTime } from '../../utils/date';
import type { AcceptanceStatus, ChangeRequest, Feature, FeatureConfirmation, PhaseContractTag } from '../../types';
import type { FeatureImageWithUrl } from '../../types/acceptance';

interface AcceptanceFeatureDetailProps {
  featureId: string;
  projectId: string;
  phaseId: string;
  /** Tên giai đoạn chứa feature này — hiển thị cạnh tiêu đề để dễ nhận biết đang ở giai đoạn nào. */
  phaseName?: string;
  /** Gọi lại sau khi trạng thái nghiệm thu của feature thay đổi (để refresh cây). */
  onStatusChanged?: (featureId: string, status: AcceptanceStatus) => void;
  /** Tăng giá trị này để âm thầm tải lại (vd. sau khi đóng dialog quản lý ảnh) mà không hiện spinner. */
  refreshToken?: number;
}

type AcceptanceChoice = 'CONFIRMED' | 'CHANGE_REQUESTED';

function imageUrl(img: FeatureImageWithUrl): string {
  return resolveImageUrl(img);
}

/**
 * Khối chi tiết một Feature trong màn hình nghiệm thu khách hàng:
 * mô tả + ảnh minh hoạ + trạng thái hiện tại + form xác nhận / yêu cầu thay đổi.
 */
export function AcceptanceFeatureDetail({
  featureId,
  phaseId,
  phaseName,
  onStatusChanged,
  refreshToken,
}: AcceptanceFeatureDetailProps): JSX.Element {
  const [feature, setFeature] = useState<(Feature & { open_bug_count?: number }) | null>(null);
  const [images, setImages] = useState<FeatureImageWithUrl[]>([]);
  const [current, setCurrent] = useState<FeatureConfirmation | null>(null);
  const [history, setHistory] = useState<FeatureConfirmation[]>([]);
  const [latestChangeRequest, setLatestChangeRequest] = useState<ChangeRequest | null>(null);
  const [openChangeRequestId, setOpenChangeRequestId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [choice, setChoice] = useState<AcceptanceChoice>('CONFIRMED');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  /** Ảnh đã chọn nhưng chưa gửi — upload sau khi mốc nghiệm thu được tạo. */
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [contractTags, setContractTags] = useState<PhaseContractTag[]>([]);
  const [assignedTagIds, setAssignedTagIds] = useState<string[]>([]);
  // Đánh số request để bỏ qua phản hồi về muộn (bấm nhanh liên tiếp) — KHÔNG
  // disable nút trong lúc chờ, tránh nhấp nháy mờ/sáng trên mỗi cú bấm.
  const toggleTagsRequestRef = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const [featureData, imageData, acceptanceData] = await Promise.all([
        featureApi.get(featureId),
        featureApi.listImages(featureId),
        acceptanceApi.get(featureId),
      ]);
      setFeature(featureData);
      setImages(imageData as FeatureImageWithUrl[]);
      setCurrent(acceptanceData.current);
      setHistory(acceptanceData.history);

      // Nhãn hợp đồng là dữ liệu bổ sung — lỗi tải (vd role tuỳ biến thiếu
      // PHASE_VIEW) không được làm trắng cả khung chi tiết, chỉ ẩn section.
      try {
        const [tagsData, assignedTagsData] = await Promise.all([
          contractTagApi.listByPhase(phaseId),
          featureApi.getContractTags(featureId),
        ]);
        setContractTags(tagsData);
        setAssignedTagIds(assignedTagsData);
      } catch {
        setContractTags([]);
        setAssignedTagIds([]);
      }

      // Yêu cầu thay đổi gần nhất của feature này (nếu có) — hiển thị trạng thái
      // xử lý (OPEN/IN_PROGRESS/RESOLVED/REJECTED) để khách theo dõi tại chỗ.
      try {
        const crs = await changeRequestsApi.list({
          project_id: undefined,
          phase_id: featureData.phase_id,
          limit: 200,
        });
        const own = crs.items
          .filter((cr) => cr.feature_id === featureId)
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        setLatestChangeRequest(own[0] ?? null);
      } catch {
        setLatestChangeRequest(null);
      }
    } catch (err) {
      setLoadError(getApiErrorMessage(err, 'Không thể tải chi tiết tính năng.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [featureId, phaseId]);

  useEffect(() => {
    setTitle('');
    setNote('');
    setChoice('CONFIRMED');
    setPendingImages([]);
    void load();
  }, [load]);

  // refreshToken tăng (vd. sau khi đóng dialog quản lý ảnh có thay đổi) → âm
  // thầm tải lại, bỏ qua lần đầu vì load() ở effect trên đã chạy rồi.
  const isFirstRefresh = useRef(true);
  useEffect(() => {
    if (isFirstRefresh.current) {
      isFirstRefresh.current = false;
      return;
    }
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const currentStatus: AcceptanceStatus = current?.status ?? 'PENDING';

  const canEditContractTags = usePermission('FEATURE_CONTRACT_TAG_UPDATE');
  const canRevoke = usePermission('FEATURE_CONFIRM_REVOKE');
  const canViewBugs = usePermission('BUG_VIEW');
  // Còn bug mở → tính năng đang "Fixing" (trạng thái phái sinh, thay chip trạng thái gốc).
  const isFixing = (feature?.open_bug_count ?? 0) > 0;
  // Từng xác nhận nhưng hiện không còn CONFIRMED (vd phát sinh bug → hệ thống
  // ghi mốc "Chờ nghiệm thu") → khách cần xác nhận lại.
  const needsReconfirm =
    currentStatus === 'PENDING' && history.some((h) => h.status === 'CONFIRMED');

  // Bug thay đổi → tải lại feature (open_bug_count) và báo cha refresh cây.
  const handleBugsChanged = () => {
    void load(true);
    onStatusChanged?.(featureId, currentStatus);
  };

  const handleToggleContractTags = async (next: string[]) => {
    const previous = assignedTagIds;
    const requestId = ++toggleTagsRequestRef.current;
    setAssignedTagIds(next);
    try {
      const saved = await featureApi.setContractTags(featureId, next);
      // Bỏ qua nếu đã có cú bấm mới hơn xen vào — tránh phản hồi về muộn đè
      // lên trạng thái mới nhất người dùng đang thấy.
      if (toggleTagsRequestRef.current === requestId) {
        setAssignedTagIds(saved);
      }
    } catch (err) {
      if (toggleTagsRequestRef.current === requestId) {
        setAssignedTagIds(previous);
        notify(getApiErrorMessage(err, 'Không thể cập nhật nhãn hợp đồng'), 'error');
      }
    }
  };

  const handleChoiceChange = (next: AcceptanceChoice) => {
    setChoice(next);
    // Điền sẵn tiêu đề theo tên tính năng để khách không phải gõ 2 chỗ —
    // vẫn cho sửa nếu muốn, chỉ điền khi ô tiêu đề đang trống.
    if (next === 'CHANGE_REQUESTED' && title.trim().length === 0 && feature) {
      setTitle(`Yêu cầu thay đổi: ${feature.name}`);
    }
  };

  const imageItems = useMemo(
    () => images.map((img) => ({ id: img.id, url: imageUrl(img), caption: img.caption })),
    [images],
  );

  const handleSubmit = async () => {
    if (choice === 'CHANGE_REQUESTED' && title.trim().length === 0) {
      notify('Vui lòng nhập tiêu đề yêu cầu thay đổi', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      // Mốc nghiệm thu phải tồn tại trước mới có id để gắn ảnh. Huỷ form giữa
      // chừng vì thế không để lại ảnh mồ côi trên R2.
      let confirmationId: string | null;
      if (choice === 'CONFIRMED') {
        confirmationId = (await acceptanceApi.confirm(featureId, note.trim())).id;
        notify('Đã xác nhận nghiệm thu tính năng', 'success');
      } else {
        const cr = await acceptanceApi.createChangeRequest(featureId, title.trim(), note.trim());
        confirmationId = cr.confirmation_id ?? null;
        notify('Đã gửi yêu cầu thay đổi', 'success');
      }

      // Ảnh lỗi thì KHÔNG rollback: feature_confirmations là append-only, mốc
      // đã ghi không gỡ được. Giữ mốc và báo rõ phần ảnh thất bại.
      if (pendingImages.length > 0) {
        if (confirmationId) {
          try {
            await confirmationImagesApi.upload(confirmationId, pendingImages);
          } catch (err) {
            notify(getApiErrorMessage(err, 'Đã gửi nhưng tải ảnh lên thất bại'), 'warning');
          }
        } else {
          notify('Đã gửi nhưng không đính được ảnh (thiếu mốc nghiệm thu)', 'warning');
        }
      }

      setTitle('');
      setNote('');
      setPendingImages([]);
      await load(true);
      onStatusChanged?.(featureId, choice);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Gửi thất bại, vui lòng thử lại'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /** Gỡ ảnh khỏi một mốc nghiệm thu (chỉ Admin — FEATURE_CONFIRM_REVOKE). */
  const handleRemoveConfirmationImage = async (image: { id: string }) => {
    try {
      await confirmationImagesApi.remove(image.id);
      await load(true);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xoá ảnh'), 'error');
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await acceptanceApi.revoke(featureId);
      notify('Đã thu hồi nghiệm thu', 'success');
      setRevokeDialogOpen(false);
      await load(true);
      onStatusChanged?.(featureId, 'PENDING');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể thu hồi nghiệm thu'), 'error');
    } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (loadError || !feature) {
    return (
      <Alert severity="error">{loadError ?? 'Không tìm thấy tính năng.'}</Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            {phaseName ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
              >
                {phaseName}
              </Typography>
            ) : null}
            <TicketCodeBadge
              ticketCode={feature.ticket_code}
              path={`/features/${feature.ticket_code ?? feature.id}`}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {feature.name}
              </Typography>
              {isFixing ? (
                <Chip
                  label={FEATURE_FIXING_META.label}
                  color={FEATURE_FIXING_META.color}
                  size="small"
                  data-testid="feature-fixing-chip"
                />
              ) : (
                <Chip
                  label={FEATURE_STATUS_META[feature.status].label}
                  color={FEATURE_STATUS_META[feature.status].color}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
            >
              {feature.description || 'Chưa có mô tả.'}
            </Typography>
          </Box>
          <Stack alignItems="flex-end" spacing={0.5}>
            <StatusBadge status={currentStatus} testId="acceptance-current-status-badge" />
            {canRevoke && currentStatus === 'CONFIRMED' && (
              <Button
                size="small"
                color="warning"
                startIcon={<UndoIcon fontSize="small" />}
                onClick={() => setRevokeDialogOpen(true)}
              >
                Thu hồi nghiệm thu
              </Button>
            )}
          </Stack>
        </Stack>

        {latestChangeRequest && (
          <Alert
            severity="info"
            icon={false}
            sx={{ mt: 2, cursor: 'pointer' }}
            onClick={() => setOpenChangeRequestId(latestChangeRequest.id)}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: latestChangeRequest.note ? 0.5 : 0 }}>
              <Typography variant="caption" fontWeight={700}>
                Yêu cầu thay đổi
              </Typography>
              <Chip
                label={CHANGE_REQUEST_STATUS_META[latestChangeRequest.status].label}
                color={
                  CHANGE_REQUEST_STATUS_META[latestChangeRequest.status].color === 'default'
                    ? 'default'
                    : CHANGE_REQUEST_STATUS_META[latestChangeRequest.status].color
                }
                size="small"
              />
            </Stack>
            {latestChangeRequest.note && <Typography variant="body2">{latestChangeRequest.note}</Typography>}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Bấm để xem chi tiết
            </Typography>
          </Alert>
        )}

        {contractTags.length > 0 && (canEditContractTags || assignedTagIds.length > 0) && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Tài liệu tham chiếu
            </Typography>
            {canEditContractTags ? (
              <ToggleButtonGroup
                value={assignedTagIds}
                onChange={(_, next: string[]) => void handleToggleContractTags(next)}
                size="small"
                sx={{
                  flexWrap: 'wrap',
                  gap: 1,
                  // ToggleButtonGroup mặc định coi các nút liền kề là một khối
                  // dính liền — nó tự ẩn viền trái + bo góc giữa các nút không
                  // phải đầu/cuối (để trông như 1 thanh nút liên tục). Vì mỗi
                  // nút ở đây cần đứng độc lập (dạng chip, có khoảng cách bằng
                  // `gap`), phải ép lại viền + bo góc đầy đủ cho MỌI nút.
                  '& .MuiToggleButtonGroup-grouped': {
                    margin: '0 !important',
                    border: '1px solid #bdbdbd !important',
                    borderRadius: '999px !important',
                    textTransform: 'none',
                  },
                }}
                data-tour="feature-contract-tags"
              >
                {contractTags.map((tag) => (
                  <ToggleButton
                    key={tag.id}
                    value={tag.id}
                    sx={(theme) => ({
                      bgcolor: 'common.white',
                      color: 'text.primary',
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        borderColor: `${theme.palette.primary.main} !important`,
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.18),
                        },
                      },
                    })}
                  >
                    {tag.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {contractTags
                  .filter((tag) => assignedTagIds.includes(tag.id))
                  .map((tag) => (
                    <Chip key={tag.id} label={tag.label} size="small" variant="outlined" />
                  ))}
              </Stack>
            )}
          </>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Ảnh minh hoạ
        </Typography>
        <Box data-tour="feature-images">
          <ImageViewer images={imageItems} />
        </Box>
      </Paper>

      {canViewBugs && (
        <FeatureBugsSection featureId={featureId} featureName={feature.name} onChanged={handleBugsChanged} />
      )}

      <Can permission="FEATURE_CONFIRM">
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nghiệm thu tính năng
          </Typography>
          {needsReconfirm && (
            <Alert severity={isFixing ? 'warning' : 'info'} sx={{ mb: 1.5 }} data-testid="reconfirm-alert">
              {isFixing
                ? 'Tính năng phát sinh lỗi sau khi đã nghiệm thu và đang được sửa. Vui lòng xác nhận lại sau khi lỗi được khắc phục.'
                : 'Nghiệm thu trước đó không còn hiệu lực (xem lý do trong Lịch sử nghiệm thu). Vui lòng kiểm tra và xác nhận lại.'}
            </Alert>
          )}
          <FormControl component="fieldset" sx={{ width: '100%' }} data-tour="feature-choice">
            <RadioGroup
              value={choice}
              onChange={(e) => handleChoiceChange(e.target.value as AcceptanceChoice)}
            >
              <FormControlLabel
                value="CONFIRMED"
                control={<Radio />}
                label={needsReconfirm ? 'Xác nhận lại' : 'Đã xác nhận'}
              />
              <FormControlLabel
                value="CHANGE_REQUESTED"
                control={<Radio />}
                label="Yêu cầu thay đổi"
              />
            </RadioGroup>
          </FormControl>

          <Stack spacing={2} sx={{ mt: 1 }}>
            {choice === 'CHANGE_REQUESTED' && (
              <TextField
                label="Tiêu đề yêu cầu thay đổi"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                required
              />
            )}
            <TextField
              label={choice === 'CONFIRMED' ? 'Ghi chú (tuỳ chọn)' : 'Mô tả chi tiết'}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              size="small"
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Ảnh đính kèm (tuỳ chọn)
              </Typography>
              <ImageUploader
                value={pendingImages}
                onChange={setPendingImages}
                disabled={submitting}
              />
            </Box>
            <Box>
              <Button
                data-tour="feature-submit"
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => void handleSubmit()}
                disabled={submitting || (choice === 'CONFIRMED' && isFixing)}
              >
                {submitting ? 'Đang gửi…' : 'Gửi'}
              </Button>
              {choice === 'CONFIRMED' && isFixing && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  Tính năng còn lỗi chưa xử lý xong — không thể xác nhận nghiệm thu cho tới khi hết lỗi "Mới"/"Đang sửa".
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      </Can>

      {history.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <HistoryIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={700}>
              Lịch sử nghiệm thu
            </Typography>
          </Stack>
          <List dense disablePadding>
            {history.map((h) => (
              <ListItem key={h.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusBadge status={h.status} size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(h.created_at)}
                      </Typography>
                    </Stack>
                  }
                  secondary={h.note || undefined}
                />
                {/* Ảnh của chính mốc này (backend trả sẵn kèm lịch sử).
                    Chỉ Admin gỡ được — lịch sử nghiệm thu là append-only. */}
                {h.images && h.images.length > 0 && (
                  <Box sx={{ mt: 0.5, ml: 1 }}>
                    <SavedImageGrid
                      images={h.images}
                      resolveUrl={resolveConfirmationImageUrl}
                      size={72}
                      onRemove={canRevoke ? (img) => void handleRemoveConfirmationImage(img) : undefined}
                    />
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {openChangeRequestId && (
        <ChangeRequestDetail
          changeRequestId={openChangeRequestId}
          onClose={() => setOpenChangeRequestId(null)}
          onUpdated={() => void load(true)}
        />
      )}

      <ConfirmDialog
        open={revokeDialogOpen}
        title="Thu hồi nghiệm thu"
        message={`Thu hồi xác nhận nghiệm thu của tính năng "${feature.name}"? Trạng thái sẽ quay lại "Chờ nghiệm thu".`}
        confirmLabel="Thu hồi"
        confirmColor="warning"
        loading={revoking}
        onConfirm={() => void handleRevoke()}
        onCancel={() => setRevokeDialogOpen(false)}
      />
    </Stack>
  );
}
